/**
 * DealerReviewsPage — Phase 4c++.
 *
 * Übersicht aller Werkstatt-Bewertungen, die das Customer-Portal gesammelt hat.
 * Händler kann Reviews freigeben (sofern Kunden-Consent vorliegt) und auf
 * Reviews antworten. Veröffentlichte Reviews erscheinen public via
 * /api/public/dealers/{id}/workshop-reviews als Trust-Signal.
 */
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
    Star, MessageSquare, CheckCircle2, EyeOff, Eye, Wrench, ArrowLeft,
    Loader2, AlertTriangle,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

function StarRow({ value, size = "w-4 h-4" }) {
    return (
        <div className="flex gap-0.5">
            {[1, 2, 3, 4, 5].map((s) => (
                <Star
                    key={s}
                    className={`${size} ${s <= value ? "fill-[#FFCC00] text-[#FFCC00]" : "text-gray-300"}`}
                />
            ))}
        </div>
    );
}

export default function DealerReviewsPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState({ reviews: [], stats: { count: 0 } });
    const [filter, setFilter] = useState("all"); // all | pending | published
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [busyId, setBusyId] = useState(null);
    const [respondId, setRespondId] = useState(null);
    const [respondText, setRespondText] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const r = await api.get(`/dealer/workshop-reviews?only=${filter}`);
            setData(r.data);
        } catch (e) {
            setError(e.response?.data?.detail || "Bewertungen konnten nicht geladen werden");
        }
        setLoading(false);
    }, [filter]);

    useEffect(() => {
        if (user === false) navigate("/login");
        if (user && user.role !== "b2b" && user.role !== "admin" && user.role !== "zweiradverkaeufer") navigate("/");
        if (user) load();
    }, [user, navigate, load]);

    const togglePublish = async (review) => {
        setBusyId(review.id);
        try {
            await api.post(
                `/dealer/workshop-reviews/${review.id}/publish`,
                { published: !review.published },
            );
            load();
        } catch (e) {
            alert(e.response?.data?.detail || "Veröffentlichung fehlgeschlagen");
        }
        setBusyId(null);
    };

    const submitResponse = async (rid) => {
        if (!respondText.trim()) return;
        setBusyId(rid);
        try {
            await api.post(
                `/dealer/workshop-reviews/${rid}/respond`,
                { response: respondText.trim() },
            );
            setRespondId(null);
            setRespondText("");
            load();
        } catch (e) {
            alert(e.response?.data?.detail || "Antwort konnte nicht gespeichert werden");
        }
        setBusyId(null);
    };

    if (!user) return <div className="p-8 text-sm text-gray-500">Lade…</div>;

    const stats = data.stats || { count: 0 };
    const reviews = data.reviews || [];

    return (
        <div className="min-h-screen bg-gray-50" data-testid="dealer-reviews-page">
            <div className="max-w-5xl mx-auto px-4 py-6 md:py-10">
                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                    <button
                        onClick={() => navigate("/dealer/workshop-orders")}
                        className="text-sm text-gray-500 hover:text-[#111827] inline-flex items-center gap-1"
                        data-testid="back-to-orders"
                    >
                        <ArrowLeft className="w-4 h-4" /> Werkstatt-Aufträge
                    </button>
                </div>
                <h1 className="font-display font-black text-3xl md:text-4xl text-[#111827]">
                    Kunden-Bewertungen
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                    Bewertungen, die Kunden über das Customer-Portal nach Abschluss der Reparatur
                    abgegeben haben.
                </p>

                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6 mb-6">
                    <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid="stat-count">
                        <div className="text-xs uppercase text-gray-500 font-bold tracking-wider">Bewertungen</div>
                        <div className="font-mono-num font-black text-2xl text-[#111827] mt-1">{stats.count || 0}</div>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid="stat-average">
                        <div className="text-xs uppercase text-gray-500 font-bold tracking-wider">Ø Rating</div>
                        <div className="font-mono-num font-black text-2xl text-[#111827] mt-1 flex items-center gap-2">
                            {stats.average !== null && stats.average !== undefined ? stats.average.toFixed(2) : "—"}
                            <Star className="w-5 h-5 fill-[#FFCC00] text-[#FFCC00]" />
                        </div>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid="stat-published">
                        <div className="text-xs uppercase text-gray-500 font-bold tracking-wider">Öffentlich</div>
                        <div className="font-mono-num font-black text-2xl text-emerald-700 mt-1">{stats.published_count || 0}</div>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid="stat-pending">
                        <div className="text-xs uppercase text-gray-500 font-bold tracking-wider">Offen</div>
                        <div className="font-mono-num font-black text-2xl text-amber-700 mt-1">{stats.pending_count || 0}</div>
                    </div>
                </div>

                {/* Filter */}
                <div className="flex gap-2 mb-4" data-testid="review-filters">
                    {[
                        ["all", "Alle", stats.count || 0],
                        ["pending", "Nicht veröffentlicht", stats.pending_count || 0],
                        ["published", "Veröffentlicht", stats.published_count || 0],
                    ].map(([key, label, count]) => (
                        <button
                            key={key}
                            onClick={() => setFilter(key)}
                            className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm border ${
                                filter === key
                                    ? "bg-[#111827] text-white border-[#111827]"
                                    : "bg-white text-[#111827] border-gray-300 hover:bg-gray-100"
                            }`}
                            data-testid={`filter-${key}`}
                        >
                            {label} <span className="font-mono-num ml-1">({count})</span>
                        </button>
                    ))}
                </div>

                {/* Errors */}
                {error && (
                    <div className="bg-red-50 border-l-4 border-red-500 px-4 py-3 text-sm text-red-800 mb-4">
                        <AlertTriangle className="w-4 h-4 inline mr-2" /> {error}
                    </div>
                )}

                {/* Reviews list */}
                {loading ? (
                    <div className="text-center py-10 text-gray-500">
                        <Loader2 className="w-6 h-6 inline animate-spin" /> Lade Bewertungen…
                    </div>
                ) : reviews.length === 0 ? (
                    <div className="bg-white border border-gray-200 rounded-sm py-12 text-center" data-testid="no-reviews">
                        <MessageSquare className="w-10 h-10 mx-auto text-gray-300" />
                        <div className="mt-2 text-sm text-gray-500">
                            {filter === "all"
                                ? "Noch keine Bewertungen erhalten. Sobald ein Auftrag abgeschlossen ist, kann der Kunde im Customer-Portal eine Bewertung abgeben."
                                : "Keine Bewertungen in dieser Kategorie."}
                        </div>
                    </div>
                ) : (
                    <div className="space-y-3" data-testid="reviews-list">
                        {reviews.map((r) => (
                            <div
                                key={r.id}
                                className="bg-white border border-gray-200 rounded-sm p-4 md:p-5"
                                data-testid={`review-card-${r.id}`}
                            >
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <div>
                                        <div className="flex items-center gap-3">
                                            <StarRow value={r.rating} size="w-5 h-5" />
                                            <span className="font-mono-num font-black text-lg text-[#111827]">{r.rating}/5</span>
                                            {r.published ? (
                                                <span className="text-[10px] uppercase tracking-wider font-bold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-sm">
                                                    Öffentlich
                                                </span>
                                            ) : (
                                                <span className="text-[10px] uppercase tracking-wider font-bold bg-gray-100 text-gray-600 px-2 py-0.5 rounded-sm">
                                                    Privat
                                                </span>
                                            )}
                                            {!r.consent_publish && (
                                                <span
                                                    className="text-[10px] uppercase tracking-wider font-bold bg-amber-100 text-amber-800 px-2 py-0.5 rounded-sm"
                                                    title="Kunde hat einer öffentlichen Anzeige nicht zugestimmt"
                                                >
                                                    kein Consent
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-xs text-gray-500 mt-1">
                                            <strong className="text-[#111827]">{r.customer_name}</strong>
                                            {" · "}
                                            <span className="font-mono-num">#{r.work_order_number}</span>
                                            {r.vehicle && <> · {r.vehicle}</>}
                                            {" · "}
                                            {r.created_at ? new Date(r.created_at).toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" }) : ""}
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => togglePublish(r)}
                                            disabled={busyId === r.id || (!r.consent_publish && !r.published)}
                                            title={!r.consent_publish ? "Kunde hat einer Veröffentlichung nicht zugestimmt" : ""}
                                            className={`text-xs font-bold px-3 py-1.5 rounded-sm uppercase tracking-wider inline-flex items-center gap-1.5 disabled:opacity-50 ${
                                                r.published
                                                    ? "bg-white border border-gray-300 hover:bg-gray-100"
                                                    : "bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827]"
                                            }`}
                                            data-testid={`btn-publish-${r.id}`}
                                        >
                                            {r.published ? (
                                                <><EyeOff className="w-3.5 h-3.5" /> Ausblenden</>
                                            ) : (
                                                <><Eye className="w-3.5 h-3.5" /> Veröffentlichen</>
                                            )}
                                        </button>
                                    </div>
                                </div>

                                {r.comment && (
                                    <blockquote className="mt-3 pl-4 border-l-2 border-gray-300 text-sm text-gray-800 italic">
                                        „{r.comment}"
                                    </blockquote>
                                )}

                                {r.complaint && (
                                    <div className="mt-2 text-xs text-gray-500">
                                        <Wrench className="w-3 h-3 inline mr-1" /> Beanstandung: {r.complaint}
                                    </div>
                                )}

                                {/* Dealer response */}
                                {r.dealer_response ? (
                                    <div
                                        className="mt-3 bg-gray-50 border-l-4 border-[#FFCC00] px-3 py-2 text-sm"
                                        data-testid={`dealer-response-${r.id}`}
                                    >
                                        <div className="text-[10px] uppercase tracking-wider font-bold text-gray-600 mb-0.5">
                                            Ihre Antwort
                                            {r.dealer_response_at && (
                                                <span className="text-gray-400 ml-2">
                                                    {new Date(r.dealer_response_at).toLocaleDateString("de-DE")}
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-gray-800">{r.dealer_response}</div>
                                    </div>
                                ) : respondId === r.id ? (
                                    <div className="mt-3 bg-gray-50 border border-gray-200 rounded-sm p-3" data-testid={`respond-form-${r.id}`}>
                                        <textarea
                                            value={respondText}
                                            onChange={(e) => setRespondText(e.target.value)}
                                            rows={3}
                                            maxLength={2000}
                                            placeholder="Antworten Sie professionell auf das Feedback…"
                                            className="w-full border border-gray-300 rounded-sm p-2 text-sm"
                                            data-testid={`respond-textarea-${r.id}`}
                                        />
                                        <div className="flex gap-2 mt-2">
                                            <button
                                                onClick={() => { setRespondId(null); setRespondText(""); }}
                                                className="text-sm px-3 py-1.5 hover:bg-gray-200 rounded-sm"
                                            >
                                                Abbrechen
                                            </button>
                                            <button
                                                onClick={() => submitResponse(r.id)}
                                                disabled={!respondText.trim() || busyId === r.id}
                                                className="bg-[#111827] hover:bg-black text-white font-black px-3 py-1.5 rounded-sm text-sm uppercase tracking-wider disabled:opacity-50"
                                                data-testid={`btn-submit-response-${r.id}`}
                                            >
                                                Antwort speichern
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <button
                                        onClick={() => { setRespondId(r.id); setRespondText(""); }}
                                        className="mt-3 text-xs font-bold uppercase tracking-wider text-gray-600 hover:text-[#111827] inline-flex items-center gap-1.5"
                                        data-testid={`btn-respond-${r.id}`}
                                    >
                                        <MessageSquare className="w-3.5 h-3.5" /> Antworten
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* Hint footer */}
                <div className="mt-8 bg-blue-50 border-l-4 border-blue-500 px-4 py-3 text-xs text-blue-900">
                    <CheckCircle2 className="w-4 h-4 inline mr-1 -mt-0.5" />
                    Freigegebene Reviews erscheinen automatisch als Trust-Signal auf Ihrer öffentlichen
                    Werkstatt-Buchungsseite. Reviews ohne Kunden-Consent können nicht veröffentlicht werden.
                </div>
            </div>
        </div>
    );
}
